import {
  Box,
  Button as ChakraButton,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useInterval,
  VStack,
} from "@chakra-ui/react";
import { IdContainer } from "@metriport/shared-internal";
import { isPatientImportDone } from "@metriport/shared/domain/patient/patient-import/status";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { orderBy } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { useParams } from "react-router-dom";
import {
  BulkPatientImportWithDebugInfo,
  createBulkPatientImport,
  getBulkPatientImport,
  getBulkPatientImports,
  uploadBulkPatientImportFile,
} from "../../../api/internal";
import { ExtendedFacility } from "../../../api/schemas/facility";
import { ExtendedOrganization } from "../../../api/schemas/organization";
import { BulkPatientImport } from "../../../api/schemas/patient";
import { FacilitiesMap } from "../../../domain/facility";
import { getErrorMessageInternal } from "../../../shared/error";
import { useAppContext } from "../../contexts/app";
import { Button } from "../../shared/Button";
import { cxDataHook } from "../../shared/state-management";
import { Table } from "../../shared/Table";
import useMetriportToast from "../../shared/toast";

dayjs.extend(duration);

const columns = [
  "id",
  "createdAt",
  "startedAt",
  "finishedAt",
  "total",
  "successful",
  "failed",
  "status",
  "dryRun_cx",
  "dryRun_ops",
  "valid",
  "invalid",
];

const statusToDownloadResult = ["completed", "failed"];

const POLLING_INTERVAL = dayjs.duration(10, "seconds");
const MAX_IMPORT_AGE = dayjs.duration(1, "day");
const NUMBER_OF_CHARS_TO_DISPLAY = 12;

const DryRunCell = ({ isDryRun }: { isDryRun: boolean | null | undefined }) => {
  if (isDryRun === undefined) return <Text>--</Text>;
  const defaultStyle = {
    textAlign: "center" as const,
  };
  if (isDryRun) {
    return (
      <Text backgroundColor={"yellow.200"} {...defaultStyle}>
        Yes
      </Text>
    );
  }
  return <Text {...defaultStyle}>No</Text>;
};

const StatusCell = ({ status }: { status: BulkPatientImport["status"] }) => {
  let backgroundColor: string | undefined;
  switch (status) {
    case "failed":
      backgroundColor = "red.200";
      break;
    case "waiting":
      backgroundColor = "yellow.200";
      break;
    case "processing":
      backgroundColor = "green.200";
      break;
    case "completed":
      backgroundColor = "gray.200";
      break;
  }
  const defaultStyle = {
    textAlign: "center" as const,
  };
  return (
    <Text backgroundColor={backgroundColor} {...defaultStyle}>
      {status}
    </Text>
  );
};

const DownloadLink = ({
  url,
  status,
}: {
  url: string | null | undefined;
  status: BulkPatientImport["status"];
}) => {
  return (
    <Box textAlign={"center"}>
      {url && statusToDownloadResult.includes(status) ? (
        <Link href={url} target="_blank" rel="noopener noreferrer">
          <Icon as={FiDownload} boxSize={5} />
        </Link>
      ) : (
        <Icon as={FiDownload} boxSize={5} color={"gray.400"} />
      )}
    </Box>
  );
};

export default function BulkPatientImportPage() {
  const { state, dispatch } = useAppContext();
  const { cxId, facilityId } = useParams();
  const toast = useMetriportToast();
  const [, setOrganization] = useState<ExtendedOrganization | undefined>(undefined);
  const [facility, setFacility] = useState<ExtendedFacility | undefined>(undefined);
  const [facilitiesMap, setFacilities] = useState<FacilitiesMap | undefined>(undefined);
  const [imports, setImports] = useState<BulkPatientImport[]>([]);
  const [isPolling, setPolling] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [dryRun, setDryRun] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<BulkPatientImportWithDebugInfo | null>(null);
  const [loadingRecord, setLoadingRecord] = useState<boolean>(false);

  const { isOpen: isOpenCreate, onOpen: onOpenCreate, onClose: onCloseCreate } = useDisclosure();
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure();

  const loadImports = useCallback(async () => {
    if (!cxId || !facilityId) return;
    try {
      setLoading(true);
      const data = await getBulkPatientImports(cxId, facilityId);
      const sortedData = orderBy(data, "createdAt", "desc");
      setImports(sortedData);
      if (data.filter(canBeStatusPolled).length > 0) {
        setPolling(true);
      } else {
        setPolling(false);
      }
    } catch (error) {
      toast.error({
        title: "Failed to load imports",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  }, [cxId, facilityId, toast]);

  const canBeStatusPolled = useCallback(({ status, createdAt }: BulkPatientImport): boolean => {
    if (isPatientImportDone(status)) return false;
    const importDate = new Date(createdAt);
    const now = new Date();
    const hoursDiff = now.getTime() - importDate.getTime();
    return hoursDiff < MAX_IMPORT_AGE.asMilliseconds();
  }, []);

  useInterval(
    async () => {
      if (loading || !cxId) return;
      await loadImports();
    },
    isPolling ? POLLING_INTERVAL.asMilliseconds() : null
  );

  useEffect(() => {
    if (state.isLoaded && cxId) {
      loadImports();
    }
  }, [state.isLoaded, cxId]);

  useEffect(() => {
    if (state.isLoaded && facilityId && cxId) {
      cxDataHook({
        state,
        dispatch,
        cxId,
        setOrganization,
        setFacilities,
        toast,
      });
    }
  }, [state.isLoaded, cxId, facilityId]);

  useEffect(() => {
    if (facilityId && facilitiesMap) {
      const fac = facilitiesMap?.[facilityId];
      if (fac) setFacility(fac);
    }
  }, [facilityId, facilitiesMap]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (
      selectedFile &&
      (selectedFile.type === "text/csv" || selectedFile.name.toLowerCase().endsWith(".csv"))
    ) {
      setFile(selectedFile);
    } else {
      toast.error({
        title: "Invalid file",
        description: "Please select a CSV file",
      });
      e.target.value = ""; // Reset the input
    }
  }

  async function handleCreateImport() {
    if (!file || !cxId || !facilityId) return;

    try {
      setUploading(true);
      const { uploadUrl } = await createBulkPatientImport(cxId, facilityId, dryRun);
      await uploadBulkPatientImportFile(uploadUrl, file);

      toast.success({
        title: "File uploaded successfully",
        description: dryRun
          ? "The dry run import process has started. You can track its progress here."
          : "The import process has started. You can track its progress here.",
      });

      onCloseCreate();
      setFile(null);
      setDryRun(false);
      await loadImports();
    } catch (error) {
      toast.error({
        title: "Failed to upload file",
        description: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setUploading(false);
    }
  }

  const handleRowClick = async (record: BulkPatientImport) => {
    if (!cxId) return;
    try {
      setLoadingRecord(true);
      const detailedRecord = await getBulkPatientImport(cxId, record.id);
      setSelectedRecord(detailedRecord);
      onDrawerOpen();
    } catch (error) {
      console.log(error);
      toast.error({
        title: "Failed to load record details",
        description: getErrorMessageInternal(error),
      });
    } finally {
      setLoadingRecord(false);
    }
  };

  return (
    <Box>
      <VStack p={10} align="start">
        <Flex w="full" justifyContent="space-between" alignItems="center">
          <Text fontSize="xl" fontWeight="bold">
            Bulk Patient Imports
          </Text>
          <Button
            id="createImport"
            onClick={onOpenCreate}
            label="Create Import"
            icon="add"
            iconSide="left"
          />
        </Flex>
        <Link href={`/customer/${cxId}/facilities/${facilityId}`}>
          <Text fontSize="md" fontWeight="bold">
            Facility: {facility?.facility.name} - {facility?.facility.id}
          </Text>
        </Link>

        <Modal isOpen={isOpenCreate} onClose={onCloseCreate} size="3xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Create Bulk Import</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>CSV File</FormLabel>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    isDisabled={uploading}
                  />
                  <FormHelperText>
                    Select a CSV file containing patient data to import
                  </FormHelperText>
                </FormControl>
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="dry-run" mb="0">
                    Dry Run
                  </FormLabel>
                  <Switch
                    id="dry-run"
                    isChecked={dryRun}
                    onChange={e => setDryRun(e.target.checked)}
                    isDisabled={uploading}
                  />
                  <FormHelperText ml={2} mb="0">
                    When enabled, the import will be processed without actually creating patients
                  </FormHelperText>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <ChakraButton onClick={onCloseCreate} isDisabled={uploading} mr={3} variant="ghost">
                Cancel
              </ChakraButton>
              <ChakraButton
                onClick={handleCreateImport}
                isLoading={uploading}
                loadingText="Uploading..."
                isDisabled={!file}
                colorScheme="blue"
              >
                Upload
              </ChakraButton>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {loading && <Spinner size="xl" />}

        <Box mt={5} w="full">
          <Table
            id="bulkImports"
            columns={columns}
            data={imports.map(imp => ({
              key: imp.id,
              id: (
                <IdContainer
                  value={imp.id}
                  entity={"Job"}
                  full={false}
                  partialMode={"right-to-left"}
                  partialLength={NUMBER_OF_CHARS_TO_DISPLAY}
                />
              ),
              dryRun_ops: <DryRunCell isDryRun={imp.paramsOps.dryRun} />,
              dryRun_cx: <DryRunCell isDryRun={imp.paramsCx.dryRun} />,
              status: <StatusCell status={imp.status} />,
              createdAt: (
                <Box
                  onClick={() => handleRowClick(imp)}
                  cursor="pointer"
                  _hover={{ bg: "gray.200" }}
                  p={2}
                  borderRadius="md"
                  textDecoration="underline"
                >
                  {formatDateTimeLocal(imp.createdAt)}
                </Box>
              ),
              startedAt: imp.startedAt ? formatDateTimeLocal(imp.startedAt) : "-",
              finishedAt: imp.finishedAt ? formatDateTimeLocal(imp.finishedAt) : "-",
              total: imp.total,
              successful: imp.successful,
              failed: imp.failed,
              valid: <DownloadLink url={imp.validEntriesUrl} status={imp.status} />,
              invalid: <DownloadLink url={imp.invalidEntriesUrl} status={imp.status} />,
            }))}
          />
        </Box>

        <Drawer isOpen={isDrawerOpen} placement="right" onClose={onDrawerClose} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton />
            <DrawerHeader>Import Record Details</DrawerHeader>
            <DrawerBody>
              {loadingRecord ? (
                <Flex justify="center" align="center" h="full">
                  <Spinner size="xl" />
                </Flex>
              ) : selectedRecord ? (
                <Box
                  as="pre"
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  overflow="auto"
                  maxH="calc(100vh - 200px)"
                  fontSize="sm"
                >
                  {JSON.stringify(selectedRecord, null, 2)}
                </Box>
              ) : (
                <Text>No record selected</Text>
              )}
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </VStack>
    </Box>
  );
}

function formatDateTimeLocal(date: string): string {
  return dayjs(date).format("MMM D, YYYY h:mm A");
}
